import { Button } from '@crowdex/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { RadioGroup, RadioGroupItem } from '@crowdex/ui/components/radio-group';
import { cn } from '@crowdex/ui/lib/utils';
import type {
  CheckpointConflict,
  CheckpointRevertResolution,
  CheckpointRevertResult,
} from '@crowdex/workspace-protocol';
import { LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  CheckpointConflicts,
  conflictResolution,
} from '@/components/desktop-message-part/checkpoint-conflicts';
import type { MessageChangedFile } from '@/lib/conversations/message-changes';
import { errorMessage } from '@/lib/error-message';
import { SHEET_ON_PHONE } from './dialog-sheet';

/** Where the edited message goes: this conversation, or a copy of it up to the message. */
export type RewindMode = 'here' | 'new';

/** `restore`: the files are put back, the conflicts decided so; `null` keeps them. */
export type RewindChoice = { restore: CheckpointRevertResolution | null; mode: RewindMode };

function Choice({ value, title, detail }: { value: string; title: string; detail: string }) {
  return (
    <RadioGroupItem value={value}>
      <span className="min-w-0">
        <span className="block font-medium text-foreground text-sm">{title}</span>
        <span className="type-meta block text-muted-foreground">{detail}</span>
      </span>
    </RadioGroupItem>
  );
}

/**
 * Sending an edited message (design D46, mobile 43): what goes away first (the turns after it,
 * from the conversation and the agent's memory, and the files they changed), then two choices:
 * undo their file changes or keep the files; edit here, or start a new conversation from here (a
 * copy with the turns up to the message, the original left as it is). Before undoing, the files
 * someone else changed since (another conversation in the same folder, the user) are listed, and
 * stay as they are unless the user says otherwise.
 */
export function RewindDialog({
  open,
  text,
  files,
  turnCount,
  onCancel,
  onPlan,
  onSend,
}: {
  open: boolean;
  /** The edited message. */
  text: string;
  /** The files the dropped turns changed. */
  files: readonly MessageChangedFile[];
  turnCount: number;
  onCancel: () => void;
  /** What undoing the files would put back, and over what (a dry run on the workspace). */
  onPlan?: () => Promise<CheckpointRevertResult | null>;
  /** Sends; a restore refused over files that changed meanwhile returns them, sending nothing. */
  onSend: (choice: RewindChoice) => Promise<CheckpointRevertResult | null | undefined>;
}) {
  // Undoing files is offered, and picked first, only when the dropped turns changed some.
  const [restoreFiles, setRestoreFiles] = useState(files.length > 0);
  const [mode, setMode] = useState<RewindMode>('here');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<readonly CheckpointConflict[]>([]);
  const [overwrite, setOverwrite] = useState(false);
  // Planned once per opening, whatever identity the callback has.
  const plan = useRef(onPlan);
  plan.current = onPlan;
  useEffect(() => {
    if (!open) return;
    setRestoreFiles(files.length > 0);
    setMode('here');
    setError(null);
    setConflicts([]);
    setOverwrite(false);
    const planRestore = plan.current;
    if (files.length === 0 || planRestore === undefined) return;
    let current = true;
    void planRestore().then(
      (plan) => {
        if (current) setConflicts(plan?.conflicts ?? []);
      },
      (cause: unknown) => {
        if (current) setError(errorMessage(cause));
      },
    );
    return () => {
      current = false;
    };
  }, [open, files.length]);

  const restoring = mode === 'here' && files.length > 0 && restoreFiles;

  async function send() {
    setError(null);
    setWorking(true);
    try {
      // A new conversation leaves this one, and its files, as they are.
      const refused = await onSend({
        restore: restoring ? conflictResolution(conflicts, overwrite) : null,
        mode,
      });
      if (refused) {
        setConflicts(refused.conflicts);
        setError('Some files changed meanwhile: check them again before sending.');
      }
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setWorking(false);
    }
  }

  const turns = turnCount === 1 ? 'The turn after it goes' : `The ${turnCount} turns after it go`;
  const changed =
    files.length === 0
      ? ''
      : ` ${turnCount === 1 ? 'It' : 'They'} changed ${files.length} ${files.length === 1 ? 'file' : 'files'}.`;

  return (
    <Dialog
      onOpenChange={(next) => {
        if (!next && !working) onCancel();
      }}
      open={open}
    >
      <DialogContent
        className={cn('gap-4 sm:max-w-lg', SHEET_ON_PHONE)}
        data-rewind-dialog
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && !working) {
            event.preventDefault();
            void send();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="type-card-title">Send the edited message?</DialogTitle>
        </DialogHeader>
        <p className="rounded-xl bg-control/70 px-3.5 py-2.5 text-foreground text-sm">{text}</p>
        <DialogDescription className="text-foreground/85 text-sm">
          {turns} away, from the conversation and from the agent's memory.{changed}
        </DialogDescription>
        {files.length > 0 && mode === 'here' && (
          <RadioGroup
            aria-label="Files"
            onValueChange={(value) => setRestoreFiles(value === 'undo')}
            value={restoreFiles ? 'undo' : 'keep'}
          >
            <Choice
              detail="Back to the files as they were"
              title="Undo their file changes too"
              value="undo"
            />
            <Choice detail="Only the messages go" title="Keep the files as they are" value="keep" />
          </RadioGroup>
        )}
        {restoring && (
          <CheckpointConflicts
            conflicts={conflicts}
            onOverwriteChange={setOverwrite}
            overwrite={overwrite}
          />
        )}
        <RadioGroup
          aria-label="Where"
          onValueChange={(value) => setMode(value === 'new' ? 'new' : 'here')}
          value={mode}
        >
          <Choice
            detail="This conversation continues from the edited message"
            title="Edit here"
            value="here"
          />
          <Choice
            detail="A copy with the turns up to this message; this one stays as it is"
            title="Start a new conversation from here"
            value="new"
          />
        </RadioGroup>
        {error !== null && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}
        <DialogFooter className="gap-2 max-md:flex-row">
          <Button
            className="rounded-control max-md:h-12 max-md:flex-1"
            disabled={working}
            onClick={onCancel}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="rounded-control max-md:h-12 max-md:flex-1"
            disabled={working}
            onClick={() => void send()}
          >
            {working && <LoaderCircle className="size-4 animate-spin" />}
            Send
            <kbd className="ml-1 rounded-md border border-current/20 px-1 font-sans text-caption opacity-70 max-md:hidden">
              ⌘↵
            </kbd>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
